import { BrowserRouter, Route, Routes, Navigate } from 'react-router';
import { getCabinet } from './cabinet';
import './App.css';
import { CssBaseline, CssVarsProvider, extendTheme } from '@mui/joy';
import { AnimatePresence } from 'framer-motion';

import MainPage from './pages/MainPage';
import Layout from './pages/Layout';
import LayoutProvider from './context';
import PartnerProvider from './PartnerContext';

import SecondQuestionnaireForm from './components/QuestionnaireForm/SecondQuestionnaireForm';
import OnboardingQuizzesModal from './components/Quiz/OnboardingQuizzesModal';
import MessagesPage from './pages/MessagesPage';
import LentaPage from './pages/LentaPage';
import QuizzesPage from './pages/QuizzesPage';
import MatchPage from './pages/MatchPage';
import UnionsListPage from './pages/UnionsListPage';
import UnionPage from './pages/UnionPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

import PartnerLayout from './pages/partner/PartnerLayout';
import PartnerCabinetPage from './pages/partner/PartnerCabinetPage';
import PortalLoginPage from './pages/portal/PortalLoginPage';
import PartnerForgotPasswordPage from './pages/partner/PartnerForgotPasswordPage';
import PartnerResetPasswordPage from './pages/partner/PartnerResetPasswordPage';

import AdminProvider from './AdminContext';
import AdminLayout from './pages/admin/AdminLayout';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminQuizzesPage from './pages/admin/AdminQuizzesPage';
import AdminQuizEditorPage from './pages/admin/AdminQuizEditorPage';
import AdminQuizStatsPage from './pages/admin/AdminQuizStatsPage';
import AdminTraitsPage from './pages/admin/AdminTraitsPage';
import AdminPayoutsPage from './pages/admin/AdminPayoutsPage';

import AdminAdsLayout from './pages/admin/ads/AdminAdsLayout';
import AdminAdsModerationPage from './pages/admin/ads/AdminAdsModerationPage';
import AdminAdsCampaignsPage from './pages/admin/ads/AdminAdsCampaignsPage';
import AdminAdsCampaignPage from './pages/admin/ads/AdminAdsCampaignPage';
import AdminAdsAccountsPage from './pages/admin/ads/AdminAdsAccountsPage';
import AdminAdsPlacementsPage from './pages/admin/ads/AdminAdsPlacementsPage';
import AdminAdsReportsPage from './pages/admin/ads/AdminAdsReportsPage';
import AdminAdsOptimizationPage from './pages/admin/ads/AdminAdsOptimizationPage';
import AdminAdsApplicationsPage from './pages/admin/ads/AdminAdsApplicationsPage';
import AdminAdsApplicationCreatePage from './pages/admin/ads/AdminAdsApplicationCreatePage';
import AdminAdsApplicationDetailPage from './pages/admin/ads/AdminAdsApplicationDetailPage';
import AdminAdsEconomicsPage from './pages/admin/ads/AdminAdsEconomicsPage';
import AdminAdsAdvertisersPage from './pages/admin/ads/AdminAdsAdvertisersPage';
import AdminAdsCapacityPage from './pages/admin/ads/AdminAdsCapacityPage';

import StartInfo from './components/StartInfo';
import RecoveryNotice from './components/RecoveryNotice';
import AdsProvider from './components/Ads/user/AdsContext';
import ComplaintModal from './components/ComplaintModal';
import SuggestionModal from './components/SuggestionModal';
import TariffModal from './components/TariffModal';
import PurchaseLikesModal from './components/PurchaseLikesModal';
import AdsLayout from './pages/partner/ads/AdsLayout';
import AdsCampaignDetailPage from './pages/partner/ads/AdsCampaignDetailPage';
import AdsCampaignsPage from './pages/partner/ads/AdsCampaignsPage';
import AdsCampaignWizardPage from './pages/partner/ads/AdsCampaignWizardPage';
import AdsBillingPage from './pages/partner/ads/AdsBillingPage';
import AdsReportsPage from './pages/partner/ads/AdsReportsPage';
import AdsRulesPage from './pages/partner/ads/AdsRulesPage';
import AdsForecastPage from './pages/partner/ads/AdsForecastPage';

import AdvertiserProvider from './AdvertiserContext';
import BusinessRegisterPage from './pages/business/BusinessRegisterPage';
import BusinessForgotPasswordPage from './pages/business/BusinessForgotPasswordPage';
import BusinessResetPasswordPage from './pages/business/BusinessResetPasswordPage';
import BusinessVerifyEmailPage from './pages/business/BusinessVerifyEmailPage';
import AdminDateTreatsPage from './pages/admin/AdminDateTreatsPage';
import AdminPlacesPage from './pages/admin/AdminPlacesPage';
import AdminVerificationsPage from './pages/admin/AdminVerificationsPage';
import AdminProfilesPage from './pages/admin/AdminProfilesPage';
import AdminPartnersPage from './pages/admin/AdminPartnersPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminReferralsPage from './pages/admin/AdminReferralsPage';
import AdminAntifraudPage from './pages/admin/AdminAntifraudPage';
import { captureRefFromUrl } from './refStore';
import { useEffect } from 'react';


const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return children;
};



export const theme = extendTheme({
    colorSchemes: {
      light: {
        palette: {
          // primary: {
          //   50: '#f9e6ff',     // Очень светлый фиолетовый (для фонов)
          //   100: '#f5d1ff',    // Светлый фиолетовый (soft вариант)
          //   200: '#e9b3ff',    // Светлый фиолетовый (hover для soft)
          //   300: '#d966ff',    // Для бордеров (outlined)
          //   400: '#cc33ff',    // Яркий акцент
          //   500: '#E901C2',    // Основной цвет (как ваш логотип)
          //   600: '#d100b0',    // Hover для solid
          //   700: '#b8009a',    // Active/pressed
          //   800: '#6d1b7b',    // Темный фиолетовый
          //   900: '#4a1253',    // Очень темный фиолетовый
          // },
          primary: {
            50: '#f5f0ff',     // Очень светлый лавандовый (почти белый с оттенком)
            100: '#e9d8ff',    // Мягкий лавандовый (для subtle/soft UI)
            200: '#d4b5ff',    // Светлый лавандовый (hover для soft)
            300: '#b388ff',    // Лавандовый для бордеров (outlined)
            400: '#9c64ff',    // Яркий лавандово-сиреневый
            500: '#8a4dff',    // Основной цвет (насыщенный лавандовый)
            600: '#7c3aed',    // Hover для solid (чуть темнее)
            700: '#6a1b9a',    // Акцентный темно-фиолетовый (для pressed)
            800: '#4a148c',    // Глубокий фиолетовый
            900: '#38006b',    // Очень темный фиолетовый (для текста/акцентов)
          },
          danger: {
            50: "#fde3e4",
            100: "#facccd",
            200: "#f49999",
            300: "#e0666b",
            400: "#cc3340",
            500: "#c30013",
            600: "#b10011",
            700: "#9e000e",
            800: "#8b000b",
            900: "#780008"
          },
          success: {
            50: "#F6FEF6",
            100: "#E3FBE3",
            200: "#C7F7C7",
            300: "#A1E8A1",
            400: "#51BC51",
            500: "#1F7A1F",
            600: "#136C13",
            700: "#0A470A",
            800: "#042F04",
            900: "#021D02"
          },
          neutral: {
            500: '#242424', // Цвет фона для всех вариантов
            600: '#2e2e2e', // Цвет фона при наведении если solid вариант
            700: '#1f1f1f', // Цвет фона для всех вариантов (при нажатии)
          },
        },
      },
    },
    // fontSize: {
    //   xl5: "5.25rem"
    // },
    breakpoints: {
      values: {
        xs: 0,
        xs2: 300,
        sm: 600,
        sm2: 800,
        md: 900,
        lg: 1200,
        xl: 1536,
        
        extrasmall: 300,
        small: 500,
        medium: 800,
        large: 1100,
      },
    },
    fontFamily: {
      body: '"Comfortaa", "ComfortaaRegular", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      display: '"Comfortaa", "ComfortaaMedium", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fallback: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    },
    components: {
      JoyButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: '.2s',
            borderRadius: theme.vars.radius.md,
            '& .MuiSvgIcon-root': { color: 'inherit' }
          })
        }
      },
      JoyTextarea: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md
          })
        }
      },
      JoyInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md
          })
        }
      },
      JoySelect: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md
          })
        }
      },
      JoyAutocomplete: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md
          })
        }
      },
      JoyIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md,
            '& .MuiSvgIcon-root': { color: 'inherit' }
          })
        }
      }
    },
});



/**
 * Маршруты dating-сайта. Обёрнуты в LayoutProvider.
 * Сам провайдер требует useLocation/useNavigate — поэтому компонент
 * рендерится ВНУТРИ <BrowserRouter> (см. App ниже).
 */
function DatingRoutes() {
    return (
      <>
        <LayoutProvider>
          {/* Внутри LayoutProvider: на экранах логина и верификации
              children не рендерятся, значит и реклама там не запросится */}
          <AdsProvider>
            <Routes>
                {/* Публичные */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />

                {/* Защищённые */}
                <Route path="/" element={<Layout />}>
                    <Route index element={<MainPage />} />
                    <Route path="chats" element={<MessagesPage />} />
                    <Route path="lenta" element={<LentaPage />} />
                    <Route path="quizzes" element={<QuizzesPage />} />
                    <Route path="match" element={<MatchPage />} />
                    <Route path="unions" element={<UnionsListPage />} />
                    <Route path="union/:unionId" element={<UnionPage />} />
                </Route>
            </Routes>

            <SecondQuestionnaireForm />
            <OnboardingQuizzesModal />
            <ComplaintModal />
            <SuggestionModal />
            <TariffModal onPaymentSuccess={() => {}} />
            <PurchaseLikesModal onPurchaseSuccess={() => {}} />
            <StartInfo />
          </AdsProvider>
        </LayoutProvider>
        <RecoveryNotice />
      </>
    );
}


/**
 * Рабочие кабинеты на lovirds.business: бизнес и партнёр в одном месте.
 *
 * Оба провайдера подняты рядом — поэтому одна страница входа умеет пустить и туда, и туда,
 * а рекламный кабинет внутри партнёрской ветки (/partner/ads) получает контекст
 * рекламодателя, без которого он раньше падал белым экраном.
 * Куки и scope токенов у кабинетов разные, так что смешаться они не могут.
 */
function PortalRoutes() {
    return (
        <AdvertiserProvider>
            <PartnerProvider>
                <Routes>
                    {/* Единая страница входа. Старые адреса входа ведут на неё же,
                        только сразу с нужной вкладкой — письма и закладки не ломаются */}
                    <Route path="/" element={<PortalLoginPage />} />
                    <Route path="/login" element={<PortalLoginPage />} />
                    <Route path="/business/login" element={<PortalLoginPage initialRole="business" />} />
                    <Route path="/partner/login" element={<PortalLoginPage initialRole="partner" />} />

                    {/* ---------- Бизнес ---------- */}
                    <Route path="/business/register" element={<BusinessRegisterPage />} />
                    <Route path="/business/forgot-password" element={<BusinessForgotPasswordPage />} />
                    <Route path="/business/reset-password" element={<BusinessResetPasswordPage />} />
                    <Route path="/business/verify" element={<BusinessVerifyEmailPage />} />

                    <Route path="/business" element={<AdsLayout />}>
                        <Route index element={<AdsCampaignsPage />} />
                        <Route path="new" element={<AdsCampaignWizardPage />} />
                        <Route path="billing" element={<AdsBillingPage />} />
                        <Route path="reports" element={<AdsReportsPage />} />
                        <Route path="rules" element={<AdsRulesPage />} />
                        <Route path="forecast" element={<AdsForecastPage />} />
                        <Route path=":campaignId" element={<AdsCampaignDetailPage />} />
                    </Route>

                    {/* ---------- Партнёр ---------- */}
                    <Route path="/partner/forgot-password" element={<PartnerForgotPasswordPage />} />
                    <Route path="/partner/reset-password" element={<PartnerResetPasswordPage />} />

                    <Route path="/partner" element={<PartnerLayout />}>
                        <Route index element={<PartnerCabinetPage />} />

                        {/* Рекламный кабинет — отдельная ветка, чтобы работали
                            прямые ссылки на кампанию и кнопка «назад» браузера */}
                        <Route path="ads" element={<AdsLayout />}>
                            <Route index element={<AdsCampaignsPage />} />
                            {/* Статический сегмент объявлен раньше параметра,
                                иначе /ads/new уйдёт в карточку кампании */}
                            <Route path="new" element={<AdsCampaignWizardPage />} />
                            {/* Возвратный адрес ЮKassa указывает сюда */}
                            <Route path="billing" element={<AdsBillingPage />} />
                            <Route path="reports" element={<AdsReportsPage />} />
                            <Route path="rules" element={<AdsRulesPage />} />
                            <Route path="forecast" element={<AdsForecastPage />} />
                            <Route path=":campaignId" element={<AdsCampaignDetailPage />} />
                        </Route>
                    </Route>
                </Routes>
            </PartnerProvider>
        </AdvertiserProvider>
    );
}


/**
 * Роутер выбирает, какой набор маршрутов рендерить.
 * Это нужно, чтобы LayoutProvider/PartnerProvider не мешали друг другу
 * (например, LayoutProvider при отсутствии токена показывает LoginPage —
 *  а на /partner это нежелательно).
 */
function AdminRoutes() {
    return (
        <AdminProvider>
            <Routes>
                <Route path="/" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminQuizzesPage />} />
                    <Route path="quizzes/:id" element={<AdminQuizEditorPage />} />
                    <Route path="quizzes/:id/stats" element={<AdminQuizStatsPage />} />
                    <Route path="traits" element={<AdminTraitsPage />} />
                    <Route path="date-treats" element={<AdminDateTreatsPage />} />
                    <Route path="places" element={<AdminPlacesPage />} />
                    <Route path="verifications" element={<AdminVerificationsPage />} />
                    <Route path="antifraud" element={<AdminAntifraudPage />} />
                    <Route path="profiles" element={<AdminProfilesPage />} />
                    <Route path="partners" element={<AdminPartnersPage />} />
                    <Route path="payouts" element={<AdminPayoutsPage />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                    <Route path="referrals" element={<AdminReferralsPage />} />
                    
                    {/* Реклама. Карточка кампании объявлена отдельным
                        маршрутом до параметрических — иначе /campaigns
                        перехватит /campaigns/12 */}
                    <Route path="ads" element={<AdminAdsLayout />}>
                        <Route index element={<AdminAdsModerationPage />} />
                        <Route path="applications" element={<AdminAdsApplicationsPage />} />
                        <Route path="applications/new" element={<AdminAdsApplicationCreatePage />} />
                        <Route path="applications/:campaignId" element={<AdminAdsApplicationDetailPage />} />
                        <Route path="campaigns" element={<AdminAdsCampaignsPage />} />
                        <Route path="campaigns/:campaignId" element={<AdminAdsCampaignPage />} />
                        <Route path="accounts" element={<AdminAdsAccountsPage />} />
                        <Route path="placements" element={<AdminAdsPlacementsPage />} />
                        <Route path="reports" element={<AdminAdsReportsPage />} />
                        <Route path="optimization" element={<AdminAdsOptimizationPage />} />
                        <Route path="economics" element={<AdminAdsEconomicsPage />} />
                        <Route path="advertisers" element={<AdminAdsAdvertisersPage />} />
                        <Route path="capacity" element={<AdminAdsCapacityPage />} />
                    </Route>
                </Route>
            </Routes>
        </AdminProvider>
    );
}


function RootRoutes() {
    const cabinet = getCabinet();

    return (
        <AnimatePresence mode="wait">
            {cabinet === 'admin'
                ? <AdminRoutes />
                : cabinet === 'portal'
                    ? <PortalRoutes />
                    : <DatingRoutes />}
        </AnimatePresence>
    );
}


function App() {
    useEffect(() => { captureRefFromUrl(); }, []);
    return (
        <BrowserRouter>
            <CssVarsProvider theme={theme}>
                <CssBaseline />
                <RootRoutes />
            </CssVarsProvider>
        </BrowserRouter>
    );
}

export default App;
